import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell, Check, CheckCheck, X, Briefcase, FileText, CalendarCheck2 } from "lucide-react";

/**
 * Notification bell + dropdown panel rendered in the Layout header.
 *
 * - Polls `GET /api/notifications/unread-count` every 60 s
 * - Clicking the bell opens a panel with the latest 30 notifications
 * - "Mark all read" + per-row check-mark
 * - Clicking a notification's link navigates and closes the panel
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const panelRef = useRef(null);

  const fetchCount = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread ?? 0);
    } catch {/* swallow — bell is informational */}
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data || []);
    } catch {/* swallow */}
    setLoading(false);
  };

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => clearInterval(id);
  }, []);

  // Close on outside click — the panel is portaled to <body>, so check BOTH
  // the bell wrapper and the panel itself (otherwise clicks inside the panel
  // count as "outside" and close it before buttons can fire).
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await fetchList();
  };

  const markOne = async (n) => {
    if (n.read_at) return;
    try {
      await api.post(`/notifications/${n.id}/read`);
      setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
      setUnread((u) => Math.max(0, u - 1));
    } catch {/* ignore */}
  };

  const markAll = async () => {
    try {
      const { data } = await api.post("/notifications/mark-all-read");
      const now = new Date().toISOString();
      setItems((cur) => cur.map((x) => ({ ...x, read_at: x.read_at || now })));
      setUnread(0);
      toast.success(`Marked ${data.marked} read`);
    } catch { toast.error("Could not mark read"); }
  };

  const iconFor = (kind) => {
    switch (kind) {
      case "job_stage_assignment":
      case "job_assignment": return Briefcase;
      case "digest": return CalendarCheck2;
      case "invoice":
      case "invoice_reminder": return FileText;
      default: return Bell;
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={toggle}
        className="relative brut-btn-secondary p-2"
        data-testid="notification-bell"
        title="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-red-600 text-white font-mono text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 border border-zinc-900"
            data-testid="notification-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[80] pointer-events-none"
          aria-hidden="true"
        >
          {/* Panel — positioned roughly under the bell. Tailwind doesn't help us
             anchor absolutely to an arbitrary element so we use position:fixed
             top:64 right:16 which lines up with our header. */}
          <div
            ref={panelRef}
            className="brut-card bg-white pointer-events-auto fixed top-[64px] right-4 w-[360px] max-w-[calc(100vw-24px)] max-h-[80vh] flex flex-col"
            data-testid="notification-panel"
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-900 bg-yellow-100 shrink-0">
              <div>
                <div className="label-mono">NOTIFICATIONS</div>
                <div className="font-heading text-sm font-bold">{unread} unread</div>
              </div>
              <div className="flex gap-1">
                {unread > 0 && (
                  <button onClick={markAll} className="brut-btn-secondary px-2 py-1 text-[10px] font-mono flex items-center gap-1" data-testid="mark-all-read-btn">
                    <CheckCheck className="w-3 h-3" /> READ ALL
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="brut-btn-secondary p-1">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading && <div className="p-6 text-center text-zinc-500 font-mono text-xs">LOADING…</div>}
              {!loading && items.length === 0 && (
                <div className="p-8 text-center text-zinc-500 font-mono text-xs">
                  ALL CAUGHT UP 🎉<br />NEW STAGE ASSIGNMENTS WILL SHOW HERE.
                </div>
              )}
              {!loading && items.map((n) => {
                const Icon = iconFor(n.kind);
                const isUnread = !n.read_at;
                const Tag = n.link ? Link : "div";
                return (
                  <Tag
                    key={n.id}
                    to={n.link || "#"}
                    onClick={() => {
                      markOne(n);
                      if (n.link) setOpen(false);
                    }}
                    className={`block px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer ${isUnread ? "bg-yellow-50" : ""}`}
                    data-testid={`notification-row-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`shrink-0 mt-0.5 ${isUnread ? "text-orange-700" : "text-zinc-400"}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm leading-tight ${isUnread ? "font-bold" : ""}`}>{n.title}</div>
                        {n.body && <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{n.body}</div>}
                        <div className="text-[10px] font-mono text-zinc-400 mt-1">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                      </div>
                      {isUnread && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); markOne(n); }}
                          className="shrink-0 text-zinc-400 hover:text-green-700 p-0.5"
                          title="Mark read"
                          data-testid={`mark-read-${n.id}`}
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </Tag>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
